import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, PartyPopper, ChevronDown, ChevronRight, Loader, Plus, RefreshCcw } from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { AdoptAsEventModal } from './AdoptAsEventModal';
import { EventStatusBadge } from '../eventteam/EventStatusBadge';
import { cn } from '../../utils/cn';
import { SkeletonLoader } from '../SkeletonLoader';
import type { EventItem } from '../../types/eventTeam';

export interface CardEventsSectionProps {
  cardId: string;
  /** Default `card.title` for the adopt modal. */
  cardTitle?: string;
  /** Default `card.description` for the adopt modal. */
  cardDescription?: string | null;
  /** Default priority (1–10) for the adopt modal. */
  cardPriority?: number;
  canWrite: boolean;
  /** Bumped by the parent (WorkspaceBoardView WS handler) on `card.event_linked`. */
  refreshNonce?: number;
  /** Reports linked events so the parent can route mutations through `*-with-sync`. */
  onEventsChanged?: (events: EventItem[]) => void;
  /** Compact rendering for tight side panels. */
  compact?: boolean;
}

/**
 * Renders the list of Event Team events bridged to a single kanban card via the
 * Event-Workspaces integration (guide §1.5). Mirrors `<CardIncidentsSection>`.
 */
export const CardEventsSection = ({
  cardId,
  cardTitle,
  cardDescription,
  cardPriority,
  canWrite,
  refreshNonce = 0,
  onEventsChanged,
  compact = false,
}: CardEventsSectionProps) => {
  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdopt, setShowAdopt] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(
    async (initial: boolean) => {
      if (!cardId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const list = await eventTeamApiService.getEventsForCard(cardId);
        setEvents(list);
        onEventsChanged?.(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load linked events');
      } finally {
        if (initial) setLoading(false);
        setRefreshing(false);
      }
    },
    [cardId, onEventsChanged],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load(false);
  }, [refreshNonce, load]);

  const count = events?.length ?? 0;

  return (
    <div className={cn(compact ? 'subcard p-3' : 'glass-card p-4')}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-left min-w-0"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-glass-500 shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-glass-500 shrink-0" />
          )}
          <PartyPopper className="w-4 h-4 text-accent-300 shrink-0" />
          <h4 className="text-sm font-semibold text-white truncate">Events</h4>
          <span className="badge badge-neutral">{count}</span>
          {refreshing && <Loader className="w-3 h-3 animate-spin text-glass-400" />}
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void load(false)}
            disabled={loading || refreshing}
            className="btn-icon btn-icon-sm"
            title="Refresh linked events"
            aria-label="Refresh linked events"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowAdopt(true)}
              className="glass-button-secondary btn-xs"
              title="Create a new event from this card and bridge them."
            >
              <Plus className="w-3.5 h-3.5" />
              Adopt as event
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <>
          {error && (
            <div className="alert alert-danger text-xs !py-2 mb-2" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="space-y-1.5" aria-busy="true" aria-label="Loading linked events">
              <SkeletonLoader className="h-9 w-full" />
              <SkeletonLoader className="h-9 w-full" />
            </div>
          ) : count === 0 ? (
            <p className="text-xs text-glass-500">No linked events.</p>
          ) : (
            <div className="space-y-1.5">
              {(events ?? []).map((event) => (
                <Link
                  key={event.id}
                  to={`/event-team/events/${encodeURIComponent(event.id)}`}
                  className="flex items-center gap-2 rounded-lg p-2 text-xs border border-glass-200/10 hover:bg-glass-200/10 hover:border-glass-200/20 transition-colors group"
                >
                  <EventStatusBadge status={event.status} type="event" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate group-hover:text-accent-300 transition-colors">
                      {event.title}
                    </div>
                    <div className="text-[10px] text-glass-500 truncate tabular-nums">
                      P{event.priority} · Created {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {showAdopt && (
        <AdoptAsEventModal
          isOpen={showAdopt}
          onClose={() => setShowAdopt(false)}
          cardId={cardId}
          defaultTitle={cardTitle}
          defaultDescription={cardDescription ?? undefined}
          defaultPriority={cardPriority}
          onSuccess={() => {
            setShowAdopt(false);
            void load(false);
          }}
        />
      )}
    </div>
  );
};
